import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { URLS } from "../../Services/Constant";
import { get, post ,deletes} from "../../Services/ApiHandler";
import { Toast } from "../../Component/Toast";
import CustomScroll from "../../Component/CustomScroll";
import { delete_icon } from "../../assets/img";
import CommonPopup from "../../Component/CommonPopup";

/* ─────────────────────────────────────────
   HELPERS
───────────────────────────────────────── */
const getGroupLabel = (dateStr) => {
  const noteDate = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const sameDay = (a, b) =>
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear();

  if (sameDay(noteDate, today)) return "Today";
  if (sameDay(noteDate, yesterday)) return "Yesterday";

  const dd = String(noteDate.getDate()).padStart(2, "0");
  const mm = String(noteDate.getMonth() + 1).padStart(2, "0");
  const yyyy = noteDate.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const formatTime = (dateStr) => {
  const d = new Date(dateStr);
  return d
    .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })
    .toUpperCase();
};

const groupNotesByDate = (notes) => {
  const map = new Map();
  notes.forEach((note) => {
    const label = note?.created_at ? getGroupLabel(note.created_at) : "Unknown";
    if (!map.has(label)) map.set(label, []);
    map.get(label).push(note);
  });
  return Array.from(map.entries()).map(([label, items]) => ({ label, items }));
};

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
const ClientNotes = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const clientId = location?.state?.clientId;
  const clientName = location?.state?.clientName;

  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletePopup, setDeletePopup] = useState(false);
const [deleteNoteId, setDeleteNoteId] = useState(null);
const [deleting, setDeleting] = useState(false);

  /* ── Fetch notes ── */
  const fetchNotes = async () => {
    if (!clientId) return;
    try {
      setLoadingNotes(true);
      const res = await get(`${URLS.CLIENT}/${clientId}/notes`);
      if (res?.status === "success") {
        setNotes(res?.data || []);
      } else {
        Toast(res?.message || "Failed to load notes");
      }
    } catch (err) {
      console.error("Error fetching notes:", err);
      Toast("Failed to load notes");
    } finally {
      setLoadingNotes(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [clientId]);

  /* ── Save note ── */
  const handleSaveNote = async () => {
    if (!noteText.trim()) return Toast("Please enter a note");
    try {
      setSaving(true);
      const res = await post(`${URLS.CLIENT}/${clientId}/notes`, {
        note: noteText.trim(),
      });
      if (res?.status === "success") {
        setNoteText("");
        fetchNotes();
        Toast("Note saved");
      } else {
        Toast(res?.message || "Failed to save note");
      }
    } catch (err) {
      console.error("Error saving note:", err);
      Toast("Failed to save note");
    } finally {
      setSaving(false);
    }
  };


  const handleDeleteNote = (id) => {
  setDeleteNoteId(id);
  setDeletePopup(true);
};

const confirmDeleteNote = async () => {
  try {
    setDeleting(true);
    const res = await deletes(`${URLS.CLIENT}/${clientId}/notes/${deleteNoteId}`);
    if (res?.status === "success") {
      setNotes((prev) => prev.filter((item) => item.id !== deleteNoteId));
      Toast("Note deleted");
    } else {
      Toast(res?.message || "Failed to delete note");
    }
  } catch (err) {
    console.error("Error deleting note:", err);
    Toast("Failed to delete note");
  } finally {
    setDeleting(false);
    setDeletePopup(false);
    setDeleteNoteId(null);
  }
};
  
  const grouped = groupNotesByDate(notes);

  return (
    <div className="dashRightView">
      <CustomScroll>
        <div className="container_overalls col-md-5" style={{ paddingTop: 0 }}>


          {/* ── Add Note Section (unchanged) ── */}
          <div
            style={{
              marginBottom: 22,
              marginTop:20,
            }}
          >
            <label
              className="f3"
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "#8F0D3d",
                display: "block",
                marginBottom: 8,
              }}
            >
              Add New Note
            </label>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Type your note here..."
              rows={3}
              style={{
                width: "100%",
                borderRadius: 8,
                border: "1.5px solid #8F0D3dCC",
                padding: "10px 12px",
                fontSize: 14,
                background: "#F0EDE6",
                color: "#1B1B1B",
                resize: "vertical",
                outline: "none",
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
              <button
                onClick={handleSaveNote}
                disabled={saving || !noteText.trim()}
                style={{
                  padding: "9px 26px",
                  borderRadius: 8,
                  border: "none",
                  background: saving || !noteText.trim() ? "#aa8c97" : "#8F0D3d",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: saving || !noteText.trim() ? "not-allowed" : "pointer",
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  transition: "background 0.2s",
                }}
              >
                {saving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      style={{ color: "#fff", width: 14, height: 14 }}
                    />
                    Saving…
                  </>
                ) : (
                  <>
                    Save Note
                  </>
                )}
              </button>
            </div>
          </div>

          {/* ── Notes List — grouped by date ── */}
          {loadingNotes ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "50px 0" }}>
              <span
                className="spinner-border"
                role="status"
                style={{ color: "#8F0D3d", width: 30, height: 30 }}
              />
            </div>
          ) : notes.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "44px 0",
                background: "#fff",
                borderRadius: 12,
                border: "1.5px dashed #d4c9c9",
              }}
            >
              <svg width="38" height="38" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 10, opacity: 0.35 }}>
                <rect x="4" y="2" width="16" height="20" rx="2" stroke="#8F0D3d" strokeWidth="1.5" />
                <path d="M8 8h8M8 12h8M8 16h5" stroke="#8F0D3d" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <p style={{ margin: 0, color: "#aaa", fontSize: 14 }}>
                No notes yet. Add one above.
              </p>
            </div>
          ) : (
            <div style={{ paddingBottom: 50 }}>
              {grouped.map(({ label, items }) => (
                <div key={label} style={{ marginBottom: 24 }}>

                  {/* Date group label */}
                  <p
                    className="f3"
                    style={{
                      margin: "0 0 10px 0",
                      fontSize: 16,
                      fontWeight: 700,
                      color: "#8F0D3d",
                    }}
                  >
                    {label}
                  </p>

                  {/* Cards */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {items.map((note, idx) => (
                      <div
                        key={note?.id || idx}
                        style={{
                          background: "#fff",
                          borderRadius: 10,
                          padding: "13px 15px 10px",
                          border: "1px solid #e8e0d8",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                        }}
                      >
                        {/* Note text */}
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                          <p
                            style={{
                              margin: 0,
                              fontSize: 14,
                              color: "#2a2a2a",
                              lineHeight: 1.6,
                              wordBreak: "break-word",
                              flex: 1,
                            }}
                          >
                            {note?.note || note?.text || note}
                          </p>

                          {/* Delete button */}
                          <button
                            onClick={() => handleDeleteNote(note?.id)}
                            title="Delete note"
                            style={{
                              flexShrink: 0,
                              background: "transparent",
                              border: "none",
                              cursor: "pointer",
                              padding: "2px 4px",
                              display: "flex",
                              alignItems: "center",
                              marginTop: 2,
                            }}
                           
                          >
                             <img src={delete_icon} alt="" className="delete-icon" />
                          </button>
                        </div>

                        {/* Time — bottom right */}
                        {note?.created_at && (
                          <p
                            style={{
                              margin: "8px 0 0 0",
                              fontSize: 12,
                              color: "#aaa",
                              textAlign: "right",
                            }}
                          >
                            {formatTime(note.created_at)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </CustomScroll>

      <CommonPopup
  isOpen={deletePopup}
  message="Are you sure, you want to delete this note?"
  onClose={() => { setDeletePopup(false); setDeleteNoteId(null); }}
  onConfirm={confirmDeleteNote}
  loading={deleting}
  confirmText="Delete"
/>
    </div>
  );
};

export default ClientNotes;